import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useUserProjectsContext } from './UserProjectsContext';
import { useUnitPreferences } from '../hooks/useUnitPreferences';
import { emailVerificationUtils } from '../utils/pendingProject';
const ControlPanelContext = createContext(undefined);
export const ControlPanelProvider = ({ children }) => {
    const { isAuthenticated } = useAuth();
    const { projects: userProjects, hasLoaded: userProjectsLoaded } = useUserProjectsContext();
    const { system } = useUnitPreferences();
    // Control-panel area display follows the app-wide Imperial/Metric switch in
    // the UnitPreferencesFooter: imperial → acres, metric → hectares. There is no
    // separate per-panel area toggle.
    const areaUnit = system === 'metric' ? 'hectares' : 'acres';
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    const [expandedSections, setExpandedSections] = useState(new Set());
    const [viewMode, setViewMode] = useState('main');
    // Track if we've initialized the expanded sections to prevent infinite loops
    const hasInitializedRef = useRef(false);
    // Set initial expanded sections based on authentication and user projects
    useEffect(() => {
        // Only set initial state if we haven't initialized yet
        if (!hasInitializedRef.current) {
            const initialSections = new Set();
            // If email verification is pending, always expand My Projects
            // If user is authenticated and has projects, expand My Projects
            // If user is not authenticated or has no projects, expand Demo Projects
            if (emailVerificationUtils.isPending() || (isAuthenticated && userProjectsLoaded && userProjects.length > 0)) {
                initialSections.add('myProjects');
            }
            else {
                initialSections.add('projects');
            }
            setExpandedSections(initialSections);
            hasInitializedRef.current = true;
        }
    }, [isAuthenticated, userProjects.length, userProjectsLoaded]);
    // Watch for auth and email verification status changes to manage sections
    useEffect(() => {
        // Only update after initial setup
        if (hasInitializedRef.current) {
            const shouldShowMyProjects = isAuthenticated || emailVerificationUtils.isPending();
            if (shouldShowMyProjects) {
                // Show My Projects if authenticated or has pending verification
                setExpandedSections(new Set(['myProjects']));
            }
            else {
                // Show Demo Projects if not authenticated and no pending verification
                setExpandedSections(new Set(['projects']));
            }
        }
    }, [isAuthenticated]); // React to auth state changes
    const toggleFolder = useCallback((folderId) => {
        setExpandedFolders(prev => {
            const newExpanded = new Set(prev);
            if (newExpanded.has(folderId)) {
                newExpanded.delete(folderId);
            }
            else {
                newExpanded.add(folderId);
            }
            return newExpanded;
        });
    }, []);
    const toggleSection = useCallback((sectionId) => {
        setExpandedSections(prevExpanded => {
            const newExpanded = new Set();
            // If clicking on a section that's not currently expanded, expand only that one
            if (!prevExpanded.has(sectionId)) {
                newExpanded.add(sectionId);
            }
            // If clicking on the currently expanded section, collapse it (newExpanded stays empty)
            return newExpanded;
        });
    }, []);
    const toggleCollapsed = useCallback(() => {
        setIsCollapsed(prev => !prev);
    }, []);
    const collapsePanel = useCallback(() => {
        setIsCollapsed(true);
    }, []);
    const switchToProjectStructureMode = useCallback(() => {
        setViewMode('project-structure');
        setExpandedSections(new Set(['structure']));
    }, []);
    const switchToMainMode = useCallback(() => {
        setViewMode('main');
        // Use same logic as initial state to determine which sections to expand
        const initialSections = new Set();
        if (emailVerificationUtils.isPending() || (isAuthenticated && userProjectsLoaded && userProjects.length > 0)) {
            initialSections.add('myProjects');
        }
        else {
            initialSections.add('projects');
        }
        setExpandedSections(initialSections);
    }, [isAuthenticated, userProjects.length, userProjectsLoaded]);
    // Land on the main view with My Projects open. Used right after a project is
    // created so the new project is visible in the list — the count-based logic in
    // switchToMainMode can't be trusted here because the refetch is still in flight
    // (userProjects.length may momentarily read 0 on a first project).
    const showMyProjects = useCallback(() => {
        setViewMode('main');
        setExpandedSections(new Set(['myProjects']));
    }, []);
    // Memoize actions separately since they're stable
    const actions = useMemo(() => ({
        toggleFolder,
        toggleSection,
        toggleCollapsed,
        collapsePanel,
        switchToProjectStructureMode,
        switchToMainMode,
        showMyProjects
    }), [
        toggleFolder,
        toggleSection,
        toggleCollapsed,
        collapsePanel,
        switchToProjectStructureMode,
        switchToMainMode,
        showMyProjects
    ]);
    // Create stable string representations for Set comparison
    const expandedFoldersKey = Array.from(expandedFolders).sort().join(',');
    const expandedSectionsKey = Array.from(expandedSections).sort().join(',');
    // Memoize state with stable Set representations
    const state = useMemo(() => ({
        isCollapsed,
        expandedFolders,
        expandedSections,
        areaUnit,
        viewMode
    }), [isCollapsed, expandedFoldersKey, expandedSectionsKey, areaUnit, viewMode]);
    // Combine state and actions
    const value = useMemo(() => ({
        state,
        actions
    }), [state, actions]);
    return (_jsx(ControlPanelContext.Provider, { value: value, children: children }));
};
export const useControlPanel = () => {
    const context = useContext(ControlPanelContext);
    if (context === undefined) {
        throw new Error('useControlPanel must be used within a ControlPanelProvider');
    }
    return context;
};
